import { renderingTools, usedVersionReact } from '@as-react/as-react';
import type { InitOptions, Resource } from 'i18next';

import { TESTING_MODE } from '../../utils/runtime';

import { CIMODE_INIT_OPTIONS } from './cimode';

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];
const STATIC_TRANSLATION_LOAD_PATH = '/i18n/resources/components/{ns}/{lng}.json';

const getDeployedTranslationLoadPath = (): string => {
  const productionPrefix = process.env.APP_ENV === 'ecomm-prd' ? 'prd/' : '';

  return `/application-resource/react/${usedVersionReact}/${productionPrefix}i18n/resources/components/{ns}/{lng}.json`;
};

const getTranslationLoadPath = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const { hostname, port } = window.location;

  if (LOCAL_HOSTNAMES.includes(hostname) && port === '4502') {
    return `http://localhost:8082${STATIC_TRANSLATION_LOAD_PATH}`;
  }

  if (LOCAL_HOSTNAMES.includes(hostname)) {
    return undefined;
  }

  return getDeployedTranslationLoadPath();
};

const getTranslationResources = async (i18nextOptions: InitOptions, language: string): Promise<Resource> => {
  try {
    const dictionariesRequests = (i18nextOptions.ns as Array<string>)
      .map(dictionary => {
        if (!i18nextOptions.backend) {
          return;
        }

        const { loadPath, queryStringParams } = i18nextOptions.backend;
        const urlBase = (loadPath as string).replace('{ns}', dictionary).replace('{lng}', language);
        const urlQueryStringParam = queryStringParams
          ? Object.keys(queryStringParams)
              .map(key => `${key}=${queryStringParams[key]}`)
              .join('&')
          : '';

        return {
          requestUrl: urlQueryStringParam ? `${urlBase}?${urlQueryStringParam}` : urlBase,
          dictionary: dictionary,
        };
      })
      .map(async props => {
        if (!props) {
          return {};
        }

        const { requestUrl, dictionary } = props;
        const response = await fetch(requestUrl);
        const data = await response.json();
        return { [`${dictionary}`]: data };
      });

    const dictionariesResults = await Promise.all(dictionariesRequests);

    return {
      [`${language}`]: Object.assign({}, ...dictionariesResults),
    };
  } catch (e) {
    console.error('[getTranslationResources] Error', e);
    return {};
  }
};

export const initI18nClient = async ({ translationLang, locale }: { translationLang: string; locale: string }) => {
  const i18nInstancePromise = await renderingTools.i18n.instanceCreator();
  const i18nInstance = i18nInstancePromise.default();

  if (process.env.TESTING_MODE === TESTING_MODE.MOCKED) {
    await i18nInstance.init(CIMODE_INIT_OPTIONS);
    return i18nInstance;
  }

  const i18nFrontendInitOptionsPromise = await renderingTools.i18n.frontEndInitOptions();
  const translationLoadPath = getTranslationLoadPath();
  const i18nFrontEndConfiguration = i18nFrontendInitOptionsPromise.default(
    usedVersionReact,
    translationLang,
    translationLoadPath
  );

  const translationResources = await getTranslationResources(i18nFrontEndConfiguration, translationLang);

  if (!locale) {
    const asNumeralPromise = await renderingTools.numeral();
    asNumeralPromise.default.changeLocal(locale);
  }

  await i18nInstance.init({
    ...i18nFrontEndConfiguration,
    ...{
      lng: translationLang,
      resources: translationResources,
    },
  });
  i18nInstance.services.pluralResolver.addRule('nl_nl', i18nInstance.services.pluralResolver.getRule('nl'));

  return i18nInstance;
};
